import $ from 'jquery'
import { includes } from 'lodash'
import 'magnific-popup'
import PropTypes from 'prop-types'
import React from 'react'
import Helmet from 'react-helmet'
import onClickOutside from 'react-onclickoutside'
import Slider from 'react-slick'
import scrollToElement from 'scroll-to-element'
import locale from '../locale'
import DirectoryEntryModel from '../Models/DirectoryEntryModel'
import AssociatedOutput from './AssociatedOutput'
import { filtersToUrl } from './Directory'

class DetailedEntry extends React.Component {
    constructor(props, context) {
        super(props, context)

        this.lightBoxIsOpen = false
    }

    componentDidMount() {
        if (this.props.scrollTo) {
            scrollToElement(`#detail-${this.props.entry.id}`, {
                offset: -50,
                duration: 600,
            })
        }

        let entry = this.props.entry

        this.magnific = $('.image').magnificPopup({
            type: 'image',
            tLoading: locale.loading,
            gallery: {
                enabled: true,
                preload: [0, 1],
                tCounter: locale.galleryCounter,
            },
            delegate: '.slick-slide:not(.slick-cloned) a',
            callbacks: {
                open: () => {
                    this.lightBoxIsOpen = true
                },
                close: () => {
                    this.lightBoxIsOpen = false
                },
            },
        })
    }

    /**
     * Calls the close callback when a click outside of this component has been detected.
     */
    handleClickOutside() {
        // ... unless the lightbox is open.
        if (this.lightBoxIsOpen) return
        if (!this.props.closeCallback) return

        this.props.closeCallback()
    }

    /**
     * Renders the rows for the entry based on its type and which
     * associated objects are present.
     */
    renderRows() {
        let entry = this.props.entry
        let rows = []

        let agency = (
            <AssociatedOutput title={locale.projectBy} associated={entry.associated.agency} />
        )
        if (agency) {
            rows.push(
                <div className="row" key="1">
                    {agency}
                </div>,
            )
        }

        let client = (
            <AssociatedOutput
                title={`${locale.client}/${locale.manufacturer}`}
                associated={entry.associated.client}
            />
        )
        if (client) {
            rows.push(
                <div className="row" key="2">
                    {client}
                </div>,
            )
        }

        if (entry.launch_year) {
            rows.push(
                <div className="row" key="3">
                    <span>{locale.year}</span>
                    <span className="line" />
                    <span className="content">{entry.launch_year}</span>
                </div>,
            )
        }

        if (entry.location) {
            rows.push(
                <div className="row" key="4">
                    <span>{locale.location}</span>
                    <span className="line" />
                    <span className="content">{entry.location}</span>
                </div>,
            )
        }

        return rows
    }

    renderTags() {
        let tags = this.props.entry.tags.map((tag, key) => {
            return (
                <a
                    href={filtersToUrl({ tag })}
                    target="_blank"
                    key={key}
                    onClick={e => {
                        if (!this.props.onTagSelection) return
                        e.preventDefault()
                        this.props.closeCallback()
                        this.props.onTagSelection(tag)
                    }}
                >
                    <span className="tag">{tag}</span>
                </a>
            )
        })

        return <p className="tags">{tags}</p>
    }

    openLightbox(event, index) {
        event.preventDefault()

        this.magnific.magnificPopup('open', index)
    }

    render() {
        let entry = this.props.entry

        let awardName = {
            iaia: 'architecture',
            iaii: 'interior',
            iaim: 'material',
            iaip: 'products',
            ia: 'awards',
            cd: 'circular',
        }[entry.award.award]
        let winnerIcon
        if (entry.award.award && entry.award.type) {
            let awardType = entry.award.type.replace(/ /g, '')
            let iconUrl = `/files/elements/winner_icons/${awardName}_${awardType}_${entry.year}.svg`
            if (!includes(['winner', 'selection', 'bestofbest'], awardType)) {
                iconUrl = iconUrl.replace(`/${awardName}_`, '/')
            }

            winnerIcon = <img className="winner-icon" src={iconUrl} />
        }

        let alt = [entry.name]
        if (entry.texts.title) alt.push(entry.texts.title)

        return (
            <div
                className="detailed-entry"
                id={`detail-${entry.id}`}
                itemScope
                itemType="http://schema.org/Product"
            >
                <meta itemProp="url" content={entry.url} />

                {this.props.closeCallback ? (
                    <Helmet titleTemplate="%s - Iconic World">
                        <title>{entry.name}</title>
                    </Helmet>
                ) : null}

                <section className="leftColumn">
                    <Slider
                        dots={true}
                        dotsClass={'thumbnails'}
                        infinite={true}
                        slidesToShow={1}
                        slidesToScroll={1}
                        swipe={true}
                        adaptiveHeight={true}
                        className="image"
                        customPaging={function(i) {
                            return <img src={entry.images[i].small} />
                        }}
                    >
                        {entry.images.map((image, index) => (
                            <a
                                href={image.original}
                                className="gallery-item"
                                key={index}
                                onClick={evt => this.openLightbox(evt, index)}
                            >
                                <img src={image.medium} alt={alt.join(' - ')} itemProp="image" />
                                <span class="caption">{image.caption}</span>
                            </a>
                        ))}
                    </Slider>
                </section>

                <section className="information directory-entry">
                    {this.renderTags()}
                    {this.props.closeCallback ? (
                        <a href="/directory" onClick={e => e.preventDefault()}>
                            <span className="close" onClick={this.props.closeCallback} />
                        </a>
                    ) : null}

                    {winnerIcon}

                    {this.props.closeCallback ? (
                        <h1 itemProp="name">{entry.name}</h1>
                    ) : (
                        <h2 itemProp="name">{entry.name}</h2>
                    )}
                    <h3 itemProp={entry.texts.description ? '' : 'description'}>
                        {entry.texts.title}
                    </h3>

                    {this.renderRows()}

                    {entry.texts.description ? (
                        <p
                            className="description"
                            itemProp="description"
                            dangerouslySetInnerHTML={{
                                __html: entry.texts.description,
                            }}
                        />
                    ) : null}
                    {entry.texts.jury ? (
                        <div className="jurytext description">
                            <h4>{locale.jurytext}</h4>
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: entry.texts.jury,
                                }}
                            />
                        </div>
                    ) : null}
                </section>
            </div>
        )
    }
}

DetailedEntry.propTypes = {
    entry: PropTypes.instanceOf(DirectoryEntryModel).isRequired,
    closeCallback: PropTypes.func,
    scrollTo: PropTypes.bool,
}

DetailedEntry.defaultProps = {
    scrollTo: false,
}

export default onClickOutside(DetailedEntry)
